import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import React from "react";

function ModalHeader({ title, subtitle, titleIcon, onClose }) {
  return (
    <DialogTitle
      sx={{
        px: 3,
        py: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={1}>
          {titleIcon && (
            <Box
              display="flex"
              alignItems="center"
              sx={{ color: "primary.main" }}
            >
              {titleIcon}
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {onClose && (
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </DialogTitle>
  );
}

/**
 * Modal – shared dialog wrapper
 *
 * Props
 * ──────────────────────────────────────
 * modalTitle            string       – dialog heading
 * subtitle              string       – optional subheading
 * titleIcon             ReactNode    – optional icon beside the title
 * open                  boolean
 * handleClose           () => void
 * children              ReactNode    – dialog body
 * width                 Breakpoint   – MUI maxWidth ('xs'|'sm'|'md'|'lg'|'xl'). Default 'md'
 * fullscreen            boolean      – render as full-screen dialog
 * action                () => ReactNode – renders DialogActions if provided
 * headerContent         ReactNode    – optional content rendered between the title bar and
 *                                      the scrollable body (e.g. a WizardStepper).
 *                                      Sits outside DialogContent so it never scrolls away.
 *                                      The slot has a bottom border so body content cannot
 *                                      visually bleed into it when the user scrolls.
 * contentSx             SxProps      – extra sx for DialogContent
 * paperSx               SxProps      – extra sx for the Dialog Paper
 * disableBackdropClose  boolean      – when true, backdrop / Escape key won't trigger onClose
 */
export default function Modal({
  modalTitle,
  subtitle,
  titleIcon,
  open,
  handleClose,
  children,
  width = "md",
  fullscreen = false,
  action,
  contentSx = {},
  paperSx = {},
  disableBackdropClose = false,
  ...rest
}) {
  const handleDialogClose = (_, reason) => {
    if (
      disableBackdropClose &&
      (reason === "backdropClick" || reason === "escapeKeyDown")
    ) {
      return;
    }
    handleClose?.();
  };

  return (
    <Dialog
      fullWidth
      maxWidth={width}
      open={open}
      onClose={handleDialogClose}
      fullScreen={fullscreen}
      PaperProps={{
        sx: { borderRadius: 2, ...paperSx },
      }}
      {...rest}
    >
      <ModalHeader
        title={modalTitle}
        subtitle={subtitle}
        titleIcon={titleIcon}
        onClose={handleClose}
      />

      <DialogContent
        sx={{
          px: 3,
          py: 3,
          ...contentSx,
        }}
        dividers
      >
        {children}
      </DialogContent>

      {action && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          {action()}
        </DialogActions>
      )}
    </Dialog>
  );
}